/**
 * RMAFaelligkeit, A2, die Fälligkeitssicht.
 *
 * Page-scoped (kein geteiltes Bauteil → JSDoc-Kopf, kein Registry-Eintrag,
 * Regel 12). Eine Lesefläche: sie zeigt ALLE offenen Fälle mit Richtung, Alter,
 * Frist und Handlung. Ein Abruf, eine Tabelle, kein Filter, kein Schreibweg.
 *
 *   GET /api/rma/faelligkeit   (Blueprint `faelligkeit_bp`, Plan §5.2)
 *
 * Warum eine eigene Seite und keine Kachel auf `/rma` (Plan §2.7 D7): `/rma`
 * ist statusgetrieben, die Fälligkeit ist eine ZWEITE ACHSE über denselben
 * Bestand. Nachbar ist `RMASlaOverview`, dieselbe Bauform für eine
 * Querschnittsfrage über offene Fälle.
 *
 * 🔴 VIER DINGE, DIE HIER NICHT VERHANDELBAR SIND:
 *
 * 1. `verteilung` kommt aus `jsonify` und ist damit ALPHABETISCH sortiert
 *    (`Eskalation`, `Nachfrage`, `keine`, `unbekannt`). Wer die Schlüssel des
 *    Objekts der Reihe nach zeigt, zeigt eine Reihenfolge, die niemand gewählt
 *    hat. Die Reihenfolge kommt deshalb AUS DER NUTZLAST
 *    (`handlung_reihenfolge`, Nachtrag 2026-09-30, Regel-16-W2), so wie
 *    `offen_tor` es schon tut. Bringt ein älterer Server das Feld nicht mit,
 *    stehen die Eimer in der Reihenfolge ihres ersten Auftretens, und die Seite
 *    sagt es laut (Regel-16-W2′: kein eingebauter Spiegel); ein Eimer, den die
 *    Reihenfolge nicht kennt, wird HINTEN angehängt statt verschluckt.
 *
 * 2. `frist_quelle === 'keine'` ist der Leerzustand IN der Zeile: `frist` ist
 *    `null`, `frist_erreicht` ist `false`. Das heisst „ungemessen", nicht
 *    „pünktlich". Die Zelle zeigt den Strich und den Grund, nie ein Datum und
 *    nie eine Null (Regel 22 Punkt 3).
 *
 * 3. Zwei Leerzustände, nicht drei (Plan §2.7 D7): `no-data` (kein offener
 *    Fall) und `error` (Abruf gescheitert, mit Wiederholung). Ein `no-match`
 *    gibt es nicht, weil es keinen Filter gibt; ein Zustand, den es nicht
 *    geben kann, wäre die vakuöse Zusicherung.
 *
 * 4. `err` ist hier zulässig, anders als in `lib/slaSummary.js`. Dort gilt:
 *    eine gewöhnliche SLA-Verletzung rendert `warn`, `err` ist gesetzlichen
 *    und Geld-Fristen vorbehalten. Diese Seite zeigt nicht die Verletzung eines
 *    Timers, sondern die HANDLUNGSZUORDNUNG: `Eskalation` heisst „ein Mensch
 *    muss entscheiden", und das ist die eine Sache, die hier rot sein darf.
 *    (Plan §5.4; die Regel-16-Prüfung soll den Unterschied sehen und nicht als
 *    Verstoss werten.)
 *
 * 5. Die Fläche ist ENGLISCH, wie ihre Nachbarn. Die Nutzlast liefert Rohwerte
 *    (`kunde`, `Eskalation`) und einen deutschen Begründungssatz. Beides wird
 *    hier übersetzt: `RICHTUNG_LABEL` und `HANDLUNG_LABEL` für die Werte,
 *    `handlungGrundText()` für den Grund. Der Grund wird aus den STRUKTURIERTEN
 *    Feldern der Zeile neu geformt (Richtung, Fristart über `SLA_KIND_LABELS`,
 *    Stunden), nicht aus `handlung_grund`: ein roher Enum in einer englischen
 *    Tabelle ist derselbe Sachverhalt in zwei Vokabularen. `handlung_grund`
 *    bleibt im Vertrag (G3 liest es), wird hier aber nicht gerendert.
 *
 * Werte kommen aus Tokens (Regel 22), Status nie nur über Farbe: jede Pille
 * trägt ein Wort. Unbekanntes trägt zusätzlich eine gestrichelte Linie
 * (`borderCue`, dieselbe Redewendung wie `situation.js` für `unmapped`), eine
 * LinienFORM, die auch ohne Farbunterscheidung trägt. Was die Linie bedeutet,
 * steht seit dem 2026-09-30 als Legende über der Tabelle (Regel-16-H2): eine
 * Linienform, die man nicht lesen kann, ist erklärt-nicht statt tragend-nicht.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Link as RouterLink } from 'react-router-dom'
import {
  Typography, Stack, Skeleton, Link, Box, useTheme, useMediaQuery,
  IconButton, Collapse, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import TableViewport from '../components/common/TableViewport'
import MobileRecordCard from '../components/common/MobileRecordCard'
import ViewHeader from '../components/ViewHeader'
import Pill from '../components/common/Pill'
import CaseTime from '../components/common/CaseTime'
import KpiRow, { KpiCell } from '../components/common/KpiRow'
import StatTile from '../components/common/StatTile'
import ResultCount from '../components/common/ResultCount'
import TableFooterSimple from '../components/common/TableFooterSimple'
import EmptyState from '../components/common/EmptyState'
import useListQuery from '../components/common/listQuery/useListQuery'
import {
  FS, SP, PANEL_RADIUS, TOUCH_TARGET_COARSE, TOUCH_TARGET_COARSE_QUADRAT,
} from '../theme/tokens'
import { v2Tone } from '../theme/theme'
import { SLA_KIND_LABELS } from './timeline/toActivityEntry'

const API = process.env.REACT_APP_API_BASE_URL

/**
 * Der Hausstrich für „kein Wert", und er ist ein Zeichen, kein Satzzeichen.
 *
 * 🔴 Als Escape geschrieben, nicht als Literal, und das ist kein Trick: derselbe
 * Codepoint als Literal wäre in der Datei von einem Gedankenstrich im Fließtext
 * nicht zu unterscheiden. Hier steht ein Anzeigezeichen, das die Hausschreibweise
 * für „unbekannt" ist (`ProductDetail.jsx:70`, `VendorsOverview.jsx:339`, siehe
 * Regel-16-H7); im Kommentar wäre derselbe Codepoint ein Typografie-Fehler. Der
 * Escape trennt beides mechanisch.
 *
 * 🔴 Nie `0` statt „unbekannt" (Regel 22 §3): eine gescheiterte Abfrage, die als
 * `0` erscheint, ist eine Entscheidungsfalle.
 */
const KEIN_WERT = '\u2014'

/**
 * Die Hausgr\u00f6\u00dfe einer Listenseite (Plan \u00a79.6). Sie ist **keine Nutzerwahl** und
 * steht deshalb nicht in der Adresse: die URL tr\u00e4gt genau `page` (\u00a73.1).
 */
const ROWS_PER_PAGE = 50

/**
 * Der **gemessene Platzbedarf** je Spalte — Inhalt ohne die zwei 16-px-Zellpolster
 * (`size="small"`), in px bei 1280.
 *
 * 🔴 **Gemessen, nicht geschätzt** (Nachbesserung 3, 2026-10-24, R15-Runde-2-C1):
 * die Fotoaufnahme `g12b3_zweig/1280_lang.png` (Stub mit langen Fristquellen)
 * zeigt den Überlauf; die Textbreiten sind daran abgelesen und an Roboto
 * kalibriert (Faktor 0,84 gegen Helvetica gleicher Größe, an vier Stellen
 * gegengeprüft). Die Zahl je Spalte ist die **längste reale Zeile** dieser
 * Zelle, plus nichts — die Reserve steckt im Anteil unten, nicht hier.
 *
 * ⚠️ `frist` ist die längste Angabe aus `SLA_KIND_LABELS`, nicht die längste
 * denkbare: „Warehouse inspection pending" (29 Zeichen, 143 px) schlägt
 * „No shipment tracking (5 days)" (30 Zeichen, 136 px — schmalere Buchstaben).
 * Eine neue, breitere Art fällt in `RMAFaelligkeit.test.jsx` auf, weil dort die
 * Länge gegen diesen Bedarf gehalten wird.
 */
export const SPALTEN_BEDARF = {
  case: 101,      // "RMA-2099-0001" (body2 14 px) und "return · open" (12 px mono)
  richtung: 72,   // Pille "Customer"
  seit: 100,      // "01.10.2026, 10:00" (body2 14 px)
  frist: 143,     // "Warehouse inspection pending" (caption 12 px)
  handlung: 127,  // Kurzgrund "our commitment is overdue" (caption 12 px)
}

/**
 * Die fünf Spalten der Tabelle, in DOM-Reihenfolge, mit ihrem **Anteil** an der
 * Tabellenbreite.
 *
 * 🔴 **Ohne feste Breiten springt die Tabelle beim Aufklappen** (Fotobefund
 * `g12b1`, 1440 px: „WAITING ON" wandert von x≈597 auf x≈518, „SINCE" bricht
 * zweizeilig um). Der Grund ist das automatische Spaltenlayout: `TableViewport`
 * setzt `table-layout: fixed` nur MIT `columns` (`TableViewport.jsx:616-628`);
 * ohne sie verteilt der Browser die Breite nach dem INHALT, und der volle
 * Begründungssatz in der Action-Spalte zieht sie beim Aufklappen breiter.
 *
 * 🔴 **Die Anteile weichen von `COLUMN_MAX[kind].max` ab — begründet, nicht aus
 * Bequemlichkeit.** Die Hausform (`ScraperList.jsx:433-439`,
 * `ProductsTable.jsx:1007-1018`) gibt der Kennung 360/1200 = **30 %** und der
 * Deadline 160/1200 = **13,3 %**. Für DIESE fünf Zellen ist das falsch: bei
 * 1280 ist die Tabelle 902 px breit, die Deadline-Zelle hatte damit 120 px
 * (88 px Inhalt), und die längste reale Quelle braucht **143 px** — im Foto
 * `g12b3_zweig/1280_lang.png` (Zeile 1 und 3) malt sie gemessen 52 px in die
 * Action-Spalte. Gleichzeitig braucht die Kennung 101 px **Inhalt** und hatte
 * 238,6 (270,6 px Zelle).
 *
 * Der Platz kommt deshalb aus der **Kennung** (30 % → 16,6 %) und geht an die
 * **Deadline** (13,3 % → 21,6 %); `Since` bekommt ebenfalls 16,6 %, damit auch
 * dort das Datum einzeilig steht (Bedarf 100 px, vorher 88). `COLUMN_MAX`
 * bleibt die Quelle der Obergrenzen-Idee und der Token-Satz unverändert; die
 * Zahlen hier sind der gemessene Bedarf dieser Zellen **plus Reserve**, und
 * `RMAFaelligkeit.test.jsx` („Spaltenbudget") hält die Rechnung fest.
 *
 * Die Summe ist **100 %**: mehr wäre ein waagerechtes Rollen bei 1280
 * (Probe „eigene 5"), weniger liesse die Tabelle schrumpfen.
 */
const SPALTEN = [
  { key: 'case', anteil: 16.6 },
  { key: 'richtung', anteil: 12.8 },
  { key: 'seit', anteil: 16.6 },
  { key: 'frist', anteil: 21.6 },
  { key: 'handlung', anteil: 32.4 },
]

/** Dieselben fünf Spalten als `columns`-Prop. */
export const SPALTEN_BREITEN = SPALTEN.map((sp) => ({ width: `${sp.anteil}%` }))

/**
 * Die Tabellenbreite bei einer Viewport-Breite — **gemessen**, nicht gerechnet
 * aus Tokens: das Panel ist `Viewport − 330` (Seitenleiste 240, Seiten- und
 * Panelpolster zusammen 90) und die Tabelle darin `Panel − 48` (zweimal 24).
 * Gegengeprüft an allen vier Fotos (`g12b3_zweig/*_grund.png`): 1280 → 902,
 * 1440 → 1062, 1920 → 1542, 2560 → 2182.
 *
 * ⚠️ **Grenze, ausdrücklich (R15-Runde-3-W1): das Ziel ist ≥ 1280 px.** Die
 * Formel gilt für die **Desktop-Tabelle** (ab `sm`, also ab 600 px); unterhalb
 * 1280 ist sie nicht gemessen. Mit festen Anteilen ist der Frist-Inhalt
 * `(Viewport − 378) × 0,216 − 32`: 600 → 16 px · 1024 → 107,5 px · 1195 →
 * 144,5 px, gegen **143 px** Bedarf. Von 600 bis rund 1188 läuft die
 * `nowrap`-Quelle deshalb **weiterhin** in die Action-Spalte (bei 1024 um rund
 * 35 px). Die Grenze folgt aus `(Viewport − 378) × 0,216 − 32 ≥ 143`, also
 * `Viewport ≥ 1188,2` — bei 1195 liegt der Inhalt mit 144,5 px bereits
 * darüber, der Überlauf endet also davor. Das ist besser als vorher — der
 * Anteil ist von 13,3 % auf 21,6 % gestiegen, also an jeder Breite schmaler
 * als der Vorzustand —, aber es ist **nicht gelöst**; gelöst ist es ab 1280.
 */
export const tabelleBreiteBei = (viewport) => viewport - 378

/** Der Inhaltsplatz einer Spalte bei einer Viewport-Breite (ohne die zwei 16-px-Polster). */
export const spaltenInhalt = (key, viewport) => {
  const anteil = SPALTEN.find((sp) => sp.key === key)?.anteil ?? 0
  return (tabelleBreiteBei(viewport) * anteil) / 100 - 32
}

/** Ton je Handlung (Plan §5.4, an der bestehenden Sprache gemessen). */
const HANDLUNG_TON = {
  Eskalation: 'err',   // wir haben unsere eigene Zusage gerissen
  Nachfrage: 'warn',   // eine fremde Frist ist gerissen
  unbekannt: 'plain',  // die Karte kennt den Status nicht
  keine: 'plain',      // nichts zu tun
}

/** Ton je Richtung (Plan §5.4). `unbekannt` ist ein Wert, kein Nichts. */
const RICHTUNG_TON = {
  kunde: 'info',
  lieferant: 'agent',
  lager: 'brand',
  sendung: 'plain',
  wir: 'warn',
  unbekannt: 'plain',
}

/**
 * Die englischen Wörter der beiden Rohwert-Spalten (Regel-16-W1/H1).
 *
 * 🔴 EINE Tabelle je Achse, und beide werden von Kachel UND Zeile gelesen. Zwei
 * Tabellen für dieselben sechs Wörter wären die zweite Lesart derselben Frage.
 *
 * Die Wörter sind an der bestehenden Sprache des Moduls gemessen, nicht
 * erfunden: „Customer", „Vendor" und „You" sind die `shortLabel`s aus
 * `rma/actors.js::ACTOR_CONFIG`, „Warehouse" und „Shipment" stehen so in
 * `RMASlaOverview` und im Bestand. `wir` heisst hier „Us", weil die Spalte
 * „Waiting on" heisst: „Waiting on: Us" ist der Satz, den die Zelle bildet, und
 * derselbe Satz steht im Begründungstext.
 *
 * Ein unbekannter Wert zeigt sein rohes Wort, humanisiert. Er verschwindet
 * nicht: ein Wert, den die Tabelle nicht kennt, ist ein Befund über die
 * Tabelle, und genau so wird er behandelt.
 */
const RICHTUNG_LABEL = {
  kunde: 'Customer',
  lieferant: 'Vendor',
  lager: 'Warehouse',
  sendung: 'Shipment',
  wir: 'Us',
  unbekannt: 'Unknown',
}

const HANDLUNG_LABEL = {
  Eskalation: 'Escalate',
  Nachfrage: 'Follow-up',
  unbekannt: 'Unknown',
  keine: 'No action',
}

const humanize = (wert) => String(wert ?? '').replace(/_/g, ' ')

/**
 * Der Text einer Rohwert-Zelle — **nie der leere String**.
 *
 * 🔴 **Der Vertrag dieser Seite, nicht eine Laune der Probe.** Die JSDoc über
 * `RICHTUNG_LABEL` sagt: „Ein unbekannter Wert zeigt sein rohes Wort,
 * humanisiert. Er verschwindet nicht." Für den leeren String brach die zweite
 * Hälfte: `humanize('')` ist `''`, die Zelle blieb **stumm** und die Pille trug
 * nur noch ihre Farbe — genau der Zustand, den P22 verbietet („Status nie über
 * Farbe allein"). Gemessen im A/B `g12b1`: `richtung: ''` im Stub färbte P22
 * auf **beiden** Breiten rot („1 Statuszellen tragen weder Text noch ein
 * benanntes Symbol"), weil Tabelle UND Karte durch dieselbe Funktion gehen.
 *
 * ⚠️ **`richtung: ''` ist heute kein Backend-Wert** (`backend/rma/faelligkeit.py`
 * Kopf: „nie None; `unbekannt` ist ein Wert"). Der Rückfall ist deshalb keine
 * Reparatur einer Live-Nutzlast, sondern die Einhaltung des eigenen Vertrags
 * für jeden Wert, den die Abbildung nicht kennt — und P22 misst den Vertrag.
 * *Widerleger:* eine Seiten-Doktrin, die eine stumme Pille erlaubt, oder ein
 * Feld, das als optional dokumentiert ist; dann wäre der Rückfall toter Code.
 *
 * 🔴 **Die gestrichelte Linie bleibt am Literal `unbekannt`** (`BORDER_CUE_SX`,
 * drei Stellen): sie ist die Aussage über den BACKEND-Wert, den die Legende
 * erklärt. Sie auf jeden unbekannten String auszudehnen änderte die Fläche für
 * `foo` mit — außerhalb dieses Ziels und ohne Probe.
 */
const richtungLabel = (r) => RICHTUNG_LABEL[r] || humanize(r) || RICHTUNG_LABEL.unbekannt
const handlungLabel = (h) => HANDLUNG_LABEL[h] || humanize(h) || HANDLUNG_LABEL.unbekannt

/** Gestrichelter Rahmen für „die Karte kennt diesen Wert nicht" (s. `situation.js`). */
const BORDER_CUE_SX = {
  borderStyle: 'dashed',
  borderColor: (t) => v2Tone(t, 'ink').ink70,
}

const toneFor = (map, wert) => map[wert] || 'plain'

/** `active_sla:case_not_acted_on` → „Case not acted on". */
function fristQuelleLabel(quelle) {
  if (!quelle) return ''
  const [, typ] = String(quelle).split(':')
  if (typ) return SLA_KIND_LABELS[typ] || typ.replace(/_/g, ' ')
  if (quelle === 'sla_due_date') return 'Due date'
  return String(quelle).replace(/_/g, ' ')
}

/** `sla_triggered_at` → „SLA started". Unbekanntes zeigt sein rohes Wort. */
const SEIT_QUELLE_LABEL = {
  sla_triggered_at: 'SLA started',
  last_status_change: 'Status change',
  created_at: 'Case created',
}
const seitQuelleLabel = (quelle) =>
  SEIT_QUELLE_LABEL[quelle] || String(quelle || '').replace(/_/g, ' ')

const statusLabel = (s) => String(s || '').replace(/_/g, ' ')

/**
 * Der Fristtyp einer Zeile als englisches Wort, oder `''`, wenn es keine Frist
 * gibt. Spiegelt `frist_typ` aus `backend/rma/faelligkeit.py::zeile()`: bei
 * `active_sla:<art>` ist es die Art, sonst der Name der Quelle.
 *
 * 🔴 `frist_quelle === 'keine'` liefert ABSICHTLICH `''` und nicht das Wort
 * „keine": der Aufrufer unterscheidet daran „es gibt eine Frist" von „es gibt
 * keine", und ein truthy „keine" hätte jeden Zweig darunter falsch gemacht.
 */
function fristArt(z) {
  const quelle = z.frist_quelle
  if (!quelle || quelle === 'keine') return ''
  return fristQuelleLabel(quelle)
}

/**
 * Der Begründungssatz in der Sprache der Tabelle (Regel-16-W1/H1).
 *
 * 🔴 Er wird aus den STRUKTURIERTEN Feldern der Zeile geformt, nicht aus
 * `handlung_grund` gelesen. Die Nutzlast liefert den Grund auf Deutsch und mit
 * dem rohen Fristart-Schlüssel darin (`Frist case_not_acted_on seit 201 h
 * gerissen`) - in einer englischen Tabelle mit `SLA_KIND_LABELS` daneben
 * ("Case not acted on") stand derselbe Sachverhalt in zwei Vokabularen,
 * unmittelbar nebeneinander. `handlung_grund` bleibt im Vertrag und wird von G3
 * gelesen; gerendert wird hier die Übersetzung.
 *
 * Die Zweige folgen genau den Zweigen von `handlung()` im Backend, damit ein
 * neuer Zweig dort hier auffällt und nicht still den letzten Satz erbt.
 *
 * 🔴 Der Zweig „keine MIT gerissener Frist" ist der Siedlungszweig, und das ist
 * eine ABLEITUNG, keine Annahme: `handlung()` liefert `Nachfrage` für wartende
 * Richtungen mit gerissener Frist, `Eskalation` für `wir`, und `unbekannt` für
 * eine unbekannte Richtung - unabhängig von der Frist. „keine" mit
 * `frist_erreicht === true` kann deshalb nur aus dem Siedlungs-Kurzschluss
 * kommen. Festgehalten wird das an der Quelle
 * (`test_keine_mit_gerissener_frist_ist_immer_die_siedlung` in
 * `backend/tests/test_a2_faelligkeit.py`), nicht hier.
 */
function handlungGrundText(z) {
  const art = fristArt(z)
  const stunden = z.frist_stunden_ueber
  // 🔴 Der Satzrahmen wird von der Vokabel getrennt (Regel-16-W1′): die
  // `SLA_KIND_LABELS` sind SUBSTANTIVPHRASEN („Case not acted on", „No shipment
  // tracking (5 days)"), und in Satzposition ergaben 12 von 13 Arten einen
  // kaputten Satz („The Case not acted on deadline is still running."). Die
  // Phrase steht deshalb nur noch INNERHALB der Anführungszeichen; der Rahmen
  // drumherum ist für jede Art derselbe.
  //
  // 🔴 `frist_quelle === 'sla_due_date'` ist der eine Fall ohne eigene Art:
  // `fristArt` liefert dort „Due date", und „The deadline for "Due date" is
  // still running." sagte dasselbe zweimal. Er bekommt den Rahmen ohne Phrase.
  const genannt = art && z.frist_quelle !== 'sla_due_date' ? `"${art}"` : ''
  // Der Rahmen für „gerissen": `Waiting on X: the "<Art>" deadline was missed by N h.`
  const gerissen = stunden != null
    ? `the ${genannt ? `${genannt} ` : ''}deadline was missed by ${stunden} h`
    : `the ${genannt ? `${genannt} ` : ''}deadline was missed`
  // Der Rahmen für „läuft noch": `The deadline for "<Art>" is still running.`
  const laeuftNoch = genannt
    ? `The deadline for ${genannt} is still running.`
    : 'The deadline is still running.'

  if (z.handlung === 'Eskalation') {
    return `Waiting on us: ${gerissen}. We missed our own promise.`
  }
  if (z.handlung === 'Nachfrage') {
    return `Waiting on ${richtungLabel(z.richtung)}: ${gerissen}.`
  }
  if (z.handlung === 'unbekannt') {
    return 'The direction map does not know this status. The case is counted, not sorted.'
  }
  if (z.handlung === 'keine' && z.frist_erreicht) {
    return (
      'A settlement signal is present (refund succeeded or return closed), but the '
      + 'close is not covered: the declared close precondition is not a pure function. '
      + 'The case stays open.'
    )
  }
  if (z.handlung === 'keine' && art) {
    return laeuftNoch
  }
  return 'No deadline set: unmeasured, not on time.'
}

/**
 * Der KURZgrund der Zeile — eine Zeile statt zwei bis vier (G12b, Plan §2.3
 * Nr. 7). Er ist die Zeile, die stehen bleibt; der volle Satz
 * (`handlungGrundText`) wandert hinter den Aufklapper.
 *
 * 🔴 **Sechs Zweige, und sie folgen DENSELBEN Zweigen wie `handlung()` im
 * Backend** (`backend/rma/faelligkeit.py:311-348`) — nicht fünf, wie die erste
 * Planfassung annahm (Grill B3): `handlung()` liefert „keine" AUCH dann, wenn
 * eine Frist noch läuft (`:342-343`), und dieser Fall fiel unter „no deadline
 * set". Und der Siedlungszweig beschreibt das SIGNAL, nicht die Frist.
 *
 * 🔴 **Kein Datum, keine Stundenzahl.** Genau das wiederholt der volle Satz;
 * der Kurzgrund sagt, WAS zu tun ist, nicht wie lange es her ist.
 *
 * 🔴 Reine Funktion, damit die Zuordnung Zweig → Text in Jest prüfbar ist und
 * nicht nur im Browser (Plan §8.3 Punkt 2). Sie liest dieselben Felder wie
 * `handlungGrundText` und baut kein zweites Vokabular: die Richtungswörter
 * kommen aus `RICHTUNG_LABEL`.
 */
export function handlungKurzText(z) {
  if (z.handlung === 'Eskalation') return 'our commitment is overdue'
  if (z.handlung === 'Nachfrage') {
    return `waiting on the ${richtungLabel(z.richtung).toLowerCase()}`
  }
  if (z.handlung === 'unbekannt') return 'direction unknown: counted, not sorted'
  if (z.handlung === 'keine' && z.frist_erreicht) {
    // Das SIEDLUNGSSIGNAL, nicht die Frist: Erstattung erfolgreich oder
    // Ruecksendung geschlossen, der Abschluss ist aber nicht gedeckt.
    return 'settlement signal: the close is not covered'
  }
  if (z.handlung === 'keine' && fristArt(z)) return 'the deadline is still running'
  return 'no deadline set'
}

// ── Bausteine ───────────────────────────────────────────────────────────────

/**
 * Die Kachelreihe über der Tabelle.
 *
 * 🔴 `KpiRow` + `KpiCell` + `StatTile` sind die geteilten Bauteile
 * (`components/common/`, Registry-Einträge vorhanden). Die erste Fassung baute
 * hier eine eigene Kachel (`Kennzahl`) und daneben ein eigenes Skelett - eine
 * dritte Kachelbauart neben zwei lebenden Nachbarn, und
 * `component-registry.md` verbietet genau das. Vorbild ist
 * `rma/components/RmaOverviewKpiHeader.jsx`, der einzige andere v2-Kopf im
 * Modul; die Anatomie ist damit dieselbe wie dort (Wert oben, Hinweis darunter,
 * Beschriftung darüber).
 *
 * 🔴 Die Kacheln skelettieren NICHT mit der Liste. Sie bekommen `loading`
 * selbst, und `StatTile` reserviert dabei Wert-, Hinweis- und Serienzeile, die
 * Reihe behält also ihre Höhe. Die EIMER selbst kommen mit der Nutzlast: ihre
 * Namen UND ihre Reihenfolge sind Server-Wissen (Regel-16-W2′), ein Vorrat
 * davon wäre ein ungeprüfter Spiegel. Die Reihe wächst deshalb einmal, wenn die
 * Antwort landet.
 *
 * 🔴 K-T1 (2026-10-09): hier stand, dass `animate` aus bleibe — `useCountUp`
 * starte bei 0 und der Wert stehe erst nach 600 ms im DOM, was die Jest-Proben
 * zeitabhängig mache. Der Anstieg ist inzwischen aus `StatTile` ENTFERNT (und
 * mit ihm der Prop), die Begründung beschreibt damit nichts mehr. Die Zahlen
 * stehen sofort; die Proben lesen sie ohne Wartezeit.
 *
 * 🔴 **2026-10-07 (UI-Durchgang 2): die Beschriftungen sind Satzschreibung.**
 *    Sie standen hier als einzige Fläche im Haus in VERSALIEN („OPEN CASES"),
 *    während die Norm (`StatTile` in der Vorgabe-Skala) Satzschreibung trägt.
 *    Die Versalien kamen aus `StatTile`s `scale="v2"` und sind dort entfernt —
 *    an EINER Stelle, nicht hier. `scale="v2"` bleibt: der Unterschied zur
 *    Vorgabe-Skala ist jetzt allein der WERT (Plus Jakarta Sans 800 in
 *    `FS.display`), nicht mehr die Beschriftung.
 *    ⚠️ Der Satz oben („Vorbild ist `RmaOverviewKpiHeader`") beschreibt weiter
 *    die ANATOMIE (Wert, Hinweis, Beschriftung) — dessen Kacheln fahren aber die
 *    Vorgabe-Skala, sind also etwas kleiner. Das ist bekannt und gewollt.
 */
function Verteilung({ data, loading, eimer }) {
  const verteilung = data?.verteilung || {}
  return (
    <KpiRow data-testid="faelligkeit-verteilung">
      <KpiCell>
        <StatTile
          scale="v2"
          loading={loading}
          reserveHint
          label="Open cases"
          value={data?.gesamt ?? 0}
          hint={
            // 🔴 „all carry a deadline" ist bei NULL Fällen formal wahr und
            // praktisch eine Aussage über null Zeilen (R16-W4, Nachbesserung 2,
            // 2026-10-24). Dann bleibt der Hinweis leer; `reserveHint` hält die
            // Zeile trotzdem, damit die Kachelreihe nicht springt.
            data && data.gesamt > 0
              ? (data.ohne_frist > 0
                ? `${data.ohne_frist} without a deadline`
                : 'all carry a deadline')
              : ''
          }
        />
      </KpiCell>
      {eimer.map((h) => (
        <KpiCell key={h}>
          <StatTile
            scale="v2"
            loading={loading}
            label={handlungLabel(h)}
            value={verteilung[h] ?? 0}
            tone={toneFor(HANDLUNG_TON, h)}
          />
        </KpiCell>
      ))}
    </KpiRow>
  )
}

function FristZelle({ z }) {
  // Der Leerzustand IN der Zeile: ungemessen, nicht pünktlich.
  if (!z.frist || z.frist_quelle === 'keine') {
    return (
      <Stack>
        <Typography variant="body2" color="text.disabled">{KEIN_WERT}</Typography>
        <Typography variant="caption" color="text.secondary">No deadline</Typography>
      </Stack>
    )
  }
  return (
    <Stack>
      <CaseTime value={z.frist} variant="absolute" />
      {/* 🔴 KEIN Umbruch INNERHALB eines Teils (R16-C1, Nachbesserung 2,
          2026-10-24): Quelle und Überzug stehen in EINER `caption`, und
          **beide** tragen `nowrap`. Der einzige Leerraum zwischen ihnen ist die
          Bruchstelle. Vorher trug nur der Überzug die Regel: „Case not acted
          on" brach mitten im Satz, und der Überzug rutschte mit dem Restwort
          „on" in die nächste Zeile (Fotos `g12b2_zweig/1440_grund.png`: drei
          Zeilen, `1280_grund.png`: vier).
          ⚠️ Die Zusage lautet deshalb **nicht** „zweizeilig", sondern: kein
          Teil bricht in sich. Vor der Breitenkorrektur (Nachbesserung 3) waren
          es bei 1280 vier Zeilen, weil zusätzlich das DATUM brach; mit den
          gemessenen Anteilen (`SPALTEN_BEDARF`, `spaltenInhalt`) stehen Datum
          **und** Quelle je einzeilig, der Überzug bekommt bei langen Quellen
          seine eigene Zeile (drei Zeilen bei 1280 und 1440, zwei bei 1920 —
          am Nach-Foto `g12b4_zweig/1280_lang.png` gemessen) und von der Probe
          „eigene 6" gehalten. Der Überlauf DAVOR — 52 px in die Action-Spalte
          — steht am Vor-Foto `g12b3_zweig/1280_lang.png` (R15-Runde-3-C1).
          🔴 Die Quelle bleibt `nowrap` und wird **nicht** gekürzt: sie passt
          rechnerisch in die Spalte (143 px Bedarf gegen 163 px Inhalt bei
          1280). Eine Ellipse wäre hier eine zweite Aussage über denselben
          Platz, und der Platz ist der bessere Weg. */}
      <Typography variant="caption" color="text.secondary">
        <Box component="span" data-testid="faelligkeit-frist-quelle" sx={{ whiteSpace: 'nowrap' }}>
          {fristQuelleLabel(z.frist_quelle)}
        </Box>
        {z.frist_erreicht && z.frist_stunden_ueber != null ? (
          <>
            {/* Die EINE Bruchstelle: ein gewöhnlicher Leerraum zwischen zwei
                `nowrap`-Teilen. Er muss AUSSERHALB beider `span`s stehen —
                innerhalb eines `nowrap`-Elements wäre er unbrechbar. */}
            {' '}
            <Box component="span" sx={{ color: 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {'· '}{z.frist_stunden_ueber} h over
            </Box>
          </>
        ) : null}
      </Typography>
    </Stack>
  )
}

function Zeile({ z }) {
  const richtungTone = toneFor(RICHTUNG_TON, z.richtung)
  const handlungTone = toneFor(HANDLUNG_TON, z.handlung)
  const [offen, setOffen] = useState(false)
  return (
    <TableRow hover data-testid={`faelligkeit-zeile-${z.case_number}`}>
      <TableCell>
        <Link component={RouterLink} to={`/rma/${z.case_id}`} variant="body2" underline="hover">
          {z.case_number}
        </Link>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', fontFamily: 'monospace', fontSize: FS.xs }}
        >
          {z.case_type} · {statusLabel(z.status)}
        </Typography>
      </TableCell>
      <TableCell>
        <Pill
          tone={richtungTone}
          nowrap
          data-testid={`faelligkeit-richtung-${z.case_number}`}
          sx={z.richtung === 'unbekannt' ? BORDER_CUE_SX : undefined}
        >
          {richtungLabel(z.richtung)}
        </Pill>
      </TableCell>
      <TableCell>
        <Stack>
          <CaseTime value={z.seit} variant="relative" relativeLocale="en" />
          <Typography variant="caption" color="text.secondary">
            {seitQuelleLabel(z.seit_quelle)}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell><FristZelle z={z} /></TableCell>
      {/* 🔴 Die `maxWidth: 380` ist entfallen (Plan §2.3 Nr. 12): sie war der
          Platzhalter für den vollen Begründungssatz, der jetzt hinter dem
          Aufklapper liegt. */}
      <TableCell>
        <Stack spacing={SP.chip} alignItems="flex-start">
          <Stack direction="row" spacing={SP.chip} alignItems="center">
            <Pill
              tone={handlungTone}
              nowrap
              data-testid={`faelligkeit-handlung-${z.case_number}`}
              sx={z.handlung === 'unbekannt' ? BORDER_CUE_SX : undefined}
            >
              {handlungLabel(z.handlung)}
            </Pill>
            <AufklapperKnopf z={z} offen={offen} onToggle={() => setOffen((o) => !o)} />
          </Stack>
          <Typography variant="caption" color="text.secondary" data-testid="faelligkeit-grund">
            {handlungKurzText(z)}
          </Typography>
          <VollerGrund z={z} offen={offen} />
        </Stack>
      </TableCell>
    </TableRow>
  )
}

/**
 * Die Textstimme der Karte — eine für alle vier Zeilen.
 *
 * 🔴 `caption` bringt `line-height: 1.66` mit — richtig für einen Satz unter
 * einem Bild, zu luftig für vier gestapelte Angaben. Auf 390 px ist der
 * Unterschied der Platz, um den eine Karte mit einem dreizeiligen
 * Begründungssatz höher wird (gerechnet ~158 px fester Teil + 16 px je Zeile,
 * `ui2-listen-karten` L.1e).
 *
 * 🔴 `FS.sm` (12,5 px) für **alle** vier Zeilen, auch den Begründungssatz. Er
 * war `FS.xs` (11,5 px) — der wichtigste Satz der Karte war damit ihr kleinster
 * Text. Der Vergleich mit dem Schreibtisch trägt die Entscheidung, und er ist
 * am CODE gemessen, nicht angenommen: die Tabelle setzt dieselbe Zeile als
 * `variant="caption"`, und die ist hier **10,5 px** — 0,75 rem gegen die
 * 14-px-Wurzel aus `theme.js:343`, in Chromium gemessen und in `tokens.js:201`
 * festgehalten. `FS.sm` liegt darüber; `FS.md` (13) wäre eine eigene Stufe über
 * den Angaben. *Widerleger:* ein Foto, auf dem die Zeile bei 390 px nicht
 * flüssig lesbar ist — es gibt keins (der Fototeil liegt beim Orchestrator).
 *
 * 🔴 EXPORTIERT seit G13a-1 Nachbesserung 5 (R16-R3-C1): die Karten-`meta`-Zeile
 * der SLA-Übersicht (`RMASlaOverview.jsx`, `LiveTypeCard`) liest dieselbe Form.
 * Eine Quelle statt einer zweiten Abschrift — die zwei Berichtsflächen sollen
 * gleich lesen, und wer diese Zeile ändert, ändert beide.
 */
export const KARTE_TEXT_SX = { lineHeight: 1.3, fontSize: FS.sm }

/**
 * Der Schluessel des Trefferziel-Bodens, EINMAL — `TOUCH_TARGET_COARSE` traegt
 * genau diesen einen. Zweimal getippt waere er zweimal zu pflegen, und faellt
 * eine der beiden Stellen je auseinander, misst die Trefferflaeche still
 * nichts mehr. Hausform: `components/common/SplitButton.jsx:8`.
 */
const GROBER_ZEIGER = '@media (pointer: coarse)'

/**
 * Die KANTE der Trefferfläche — **44 px, aus dem Quadrat-Token**.
 *
 * 🔴 `TOUCH_TARGET_COARSE` trägt genau **eine** Achse (`minHeight`, `tokens.js`)
 * — es ist der eindimensionale Zeilenboden. Der Haus-Token für eine Fläche, die
 * BEIDE Achsen braucht, ist `TOUCH_TARGET_COARSE_QUADRAT` (`minHeight` **und**
 * `minWidth`, `tokens.js`); ein runder `IconButton` bekäme mit `minHeight`
 * allein ein Oval. Bis zum A/B `g12b1` stand hier
 * `TOUCH_TARGET_COARSE[GROBER_ZEIGER].minWidth` — ein Schlüssel, den **dieser**
 * Token nicht hat. Der Ausdruck war `undefined`, Emotion liess die Deklaration
 * fallen, und die `::after`-Fläche war **0 px breit** (44 hoch): der Punkt
 * 21 px links der Knopfmitte traf die Handlungs-Pill statt des Knopfes
 * (`ui2-listen-karten` L.1j, 390 px). Die Kante kommt deshalb aus dem
 * Quadrat-Token — kein zweites Literal daneben.
 *
 * 🔴 **Der Preis, gerechnet und am Kode gelesen:** die Fläche wächst um den
 * 32er-Kasten nach aussen, also `(44 − 32) / 2` = **6 px** je Seite, während
 * `SP.chip` die Pille nur **4 px** entfernt hält (`tokens.js`). Die Fläche
 * überlappt damit **2 px** des Pillen-Kastens. Das Pillen-POLSTER ist 10–12 px
 * (`Pill.jsx`, `px: 1.25|1.5`), der Text liegt also weit innerhalb; die Pille
 * ist zudem kein Bedienelement (kein `onClick`) — es geht keine Trefferfläche
 * verloren, und `::after` trägt keinen Grund, deckt also nichts ab.
 * *Widerleger:* ein Pillentext, der vom Knopf berührt wird (Pillen-Polster
 * unter 6 px), oder ein klickbares Element 4 px neben dem Knopf.
 */
const TREFFER_KANTE = TOUCH_TARGET_COARSE_QUADRAT[GROBER_ZEIGER].minWidth

/**
 * Die Kante des Aufklapper-Knopfes (Plan §2.3 Nr. 7). **32 px, nicht 44** — und
 * das ist die eine Zahl, an der die Höhengrenze der Karte hängt: der Haus-Boden
 * `CONTROL_HEIGHT_SX` liefert auf dem getesteten 390-px-Viewport **44** px, die
 * Kopfzeile wüchse um 15 px und der feste Teil der Karte landete über
 * `KARTE_FEST_MAX_H` (Plan §2.3, Grill W2). Die 44er-TREFFERFLÄCHE entsteht
 * stattdessen als `::after` um den Kasten — sie wächst nach aussen, ohne am
 * Layout zu rücken (dieselbe Bauart wie der Fallnummer-Link, `:487-511`).
 */
const AUFKLAPPER_KANTE = 32

/**
 * Die `sx` des Aufklapper-Knopfes — **beide Medienblöcke sind der Kern** (Plan
 * §2.3 Nr. 7, Grill W1): ohne sie gewinnen die Theme-Böden
 * (`theme.js:1097-1125`, `down('sm')` → `minHeight: 44`). Gemessen ist nur, dass
 * die FLACHE Form verliert (`tokens.js:710-764`); dass ein `sx`-Medienblock den
 * Theme-Medienblock schlägt, entscheidet der A/B `L.1e`/`L.1j`. Schlägt der
 * Theme-Block, ist der Rückfall die Spezifität — dieselben zwei Blöcke unter
 * `'&&'` (Plan, Karte K-4) —, nicht die Grenze.
 *
 * 🔴 `overflow: 'visible'` ist ein GÜRTEL, nicht die Bedingung: MUI 7.3.5 trägt
 * am `IconButton`-Träger kein `overflow` (gemessen, Plan WV7/Grill B1). Er steht
 * da für künftige Fassungen, die eines einführen könnten.
 */
const aufklapperSx = (theme) => ({
  position: 'relative',
  width: AUFKLAPPER_KANTE,
  height: AUFKLAPPER_KANTE,
  minWidth: AUFKLAPPER_KANTE,
  minHeight: AUFKLAPPER_KANTE,
  overflow: 'visible',
  [theme.breakpoints.down('sm')]: {
    minWidth: AUFKLAPPER_KANTE,
    minHeight: AUFKLAPPER_KANTE,
  },
  [GROBER_ZEIGER]: {
    minWidth: AUFKLAPPER_KANTE,
    minHeight: AUFKLAPPER_KANTE,
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: TREFFER_KANTE,
      height: TREFFER_KANTE,
      transform: 'translate(-50%, -50%)',
    },
  },
})

/** Die Kennung des aufgeklappten Kastens — EINE Stelle für `aria-controls` und `id`. */
const vollGrundId = (nr) => `faelligkeit-grund-voll-${nr}`

/**
 * Der Aufklapper — EINE Mechanik für Tabelle und Karte (Plan §2.3 Nr. 7/B4).
 *
 * 🔴 Kein Tooltip: der ist auf Touch nicht erreichbar. Der Knopf trägt
 * `aria-expanded` und `aria-controls`, und sein Name nennt die ZEILE („Why this
 * action, RMA-2026-0458") — in einer Liste hiesse ein fester Name fünfzigmal
 * dasselbe (BS18, `pattern-library.md` §Zeilenaktion).
 *
 * 🔴 **`aria-controls` NUR im offenen Zustand** (R16-W2, Nachbesserung 2,
 * 2026-10-24): der Kasten trägt `unmountOnExit` (`VollerGrund`), geschlossen
 * steht er also gar nicht im Baum — eine gesetzte Referenz zeigte auf ein
 * nicht existierendes Element. `aria-expanded` trägt den Zustand allein; die
 * Referenz ist eine Zusatzangabe über das ZIEL und darf nur stehen, wenn es das
 * Ziel gibt. Rotweg: die Bedingung entfernen — dann trägt der geschlossene
 * Knopf wieder eine tote Kennung (Probe im Jest).
 *
 * 🔴 Den ZUSTAND hält der Aufrufer (`Zeile`/`Karte`), nicht der Knopf: auf der
 * Karte sitzt der Knopf in `actions`, der Kasten in `expanded` — zwei Plätze,
 * ein Zustand.
 */
function AufklapperKnopf({ z, offen, onToggle }) {
  return (
    <IconButton
      size="small"
      onClick={onToggle}
      aria-expanded={offen}
      aria-controls={offen ? vollGrundId(z.case_number) : undefined}
      aria-label={`Why this action, ${z.case_number}`}
      sx={aufklapperSx}
    >
      {offen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    </IconButton>
  )
}

/**
 * Der aufgeklappte Teil: der VOLLE Begründungssatz (`handlungGrundText`).
 *
 * 🔴 `unmountOnExit` — im geschlossenen Zustand darf der Kasten keinen
 * fokussierbaren und keinen angesagten Inhalt behalten (`MobileRecordCard`
 * JSDoc zu `expanded`, offener Punkt aus G4b).
 */
function VollerGrund({ z, offen }) {
  return (
    <Collapse in={offen} unmountOnExit id={vollGrundId(z.case_number)}>
      <Typography
        data-testid="faelligkeit-grund-voll"
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', ...KARTE_TEXT_SX }}
      >
        {handlungGrundText(z)}
      </Typography>
    </Collapse>
  )
}

/**
 * Dieselbe Zeile als **Karte** (M5-8, Bestandsaufnahme).
 *
 * 🔴 Warum es die Karte gibt: bei 390 px ist die Tabelle eine waagerechte
 * Rollfläche, und die fünfte Spalte — die **Frist** — steht außerhalb des
 * Bildes. Genau die Angabe, für die diese Seite existiert, war ohne Wischen
 * nicht zu sehen. Die Karte trägt dieselben Felder untereinander.
 *
 * 🔴 Sie trägt **dieselben `data-testid`s** wie die Tabellenzeile
 * (`faelligkeit-zeile-<nr>`, `-richtung-`, `-handlung-`), und das ist
 * Vorschrift, keine Bequemlichkeit: die Proben greifen über diese Marken zu.
 * Eine Karte ohne sie ließe `tests/rma/faelligkeit.spec.ts` auf dem Telefon
 * **still** durchfallen — sie fände nichts und überspränge sich (Regel 15
 * §Rückbau, „grün, weil etwas fehlt").
 *
 * 🔴 Die **Fallnummer steht vorn** — sie ist der Datensatz und der Weg hinein,
 * wie die erste Spalte der Tabelle. Die **Richtung** stand als linkes Merkmal
 * davor und schob sie nach rechts; sie sitzt jetzt in `chips` unter den
 * Angaben. Die übrigen Felder folgen der Anatomie der Karte (`actions` rechts
 * im Kopf, dann `meta`, dann `chips`), nicht einer neuen Lesart.
 */
function Karte({ z }) {
  const richtungTone = toneFor(RICHTUNG_TON, z.richtung)
  const handlungTone = toneFor(HANDLUNG_TON, z.handlung)
  const fristOffen = Boolean(z.frist) && z.frist_quelle !== 'keine'
  const [offen, setOffen] = useState(false)
  return (
    <MobileRecordCard
      data-testid={`faelligkeit-zeile-${z.case_number}`}
      // 🔴 Der Titel traegt, was der Fall IST (`case_type · status`); die
      // Fallnummer wandert in den `id`-Slot und bleibt dort der Weg hinein
      // (G12b, Plan §2.3 Nr. 9). P20/P15 greifen mobil ueber
      // `[data-testid^="karte-kennung-"]` zu — ohne `fieldTestIds.id` zaehlen
      // sie 0 Karten.
      title={
        <Typography variant="caption" color="text.secondary" sx={KARTE_TEXT_SX}>
          {z.case_type} · {statusLabel(z.status)}
        </Typography>
      }
      id={
        // 🔴 `nowrap`: eine Fallnummer ist eine Kennung, kein Fließtext. Bricht
        // sie um, ist sie nicht mehr als eine Nummer zu lesen (dieselbe
        // Vorschrift wie Teil 4, E4).
        <Link
          component={RouterLink}
          to={`/rma/${z.case_id}`}
          variant="body2"
          underline="hover"
          sx={{
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            // 🔴 Die Fallnummer ist das EINZIGE Bedienelement der Karte, und
            // ihr Kasten ist die Textzeile (~18 px). Auf grobem Zeiger fehlt
            // ihr damit mehr als die Hälfte des Haus-Bodens (44 px, Regel 22
            // §8). `TOUCH_TARGET_COARSE` als KASTEN hebt aber die Kopfzeile —
            // und damit jede Karte — um rund 26 px; der feste Teil der Karte
            // (ohne Begründungssatz) ist gerechnet ~158 px und hat 12 px Luft
            // bis zur Grenze von 170 px (`ui2-listen-karten` L.1e).
            // Dieselbe Höhe deshalb als FLÄCHE um den Kasten gelegt: sie wächst
            // nach aussen, ohne am Layout zu rücken — dieselbe Bauart wie
            // `rma/components/RecommendationCard.jsx:303-304` und
            // `rma/components/SimilarCasesChips.jsx:126`. Die Zahl kommt aus
            // dem Token, nicht als zweites Literal daneben (Hausform wie
            // `SplitButton.jsx:27-28`).
            position: 'relative',
            [GROBER_ZEIGER]: {
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: TOUCH_TARGET_COARSE[GROBER_ZEIGER].minHeight,
                transform: 'translateY(-50%)',
              },
            },
          }}
        >
          {z.case_number}
        </Link>
      }
      // 🔴 ZWEI Kinder im `actions`-Slot, in DIESER Folge: Pill, dann
      // Aufklapper (Plan §2.3 Nr. 9/WV6 W6). Die Anordnung steht hier im
      // Aufrufer und nicht im geteilten Baustein: `MobileRecordCard` gibt den
      // Slot als `Box` ohne Anordnung aus, und ein `display: flex` dort
      // aenderte das Layout JEDES anderen Kartenaufrufers mit zwei Kindern.
      actions={
        <Stack direction="row" spacing={SP.chip} alignItems="center">
          <Pill
            tone={handlungTone}
            nowrap
            data-testid={`faelligkeit-handlung-${z.case_number}`}
            sx={z.handlung === 'unbekannt' ? BORDER_CUE_SX : undefined}
          >
            {handlungLabel(z.handlung)}
          </Pill>
          <AufklapperKnopf z={z} offen={offen} onToggle={() => setOffen((o) => !o)} />
        </Stack>
      }
      meta={
        <Stack spacing={SP.chip}>
          <Typography variant="caption" color="text.secondary" sx={KARTE_TEXT_SX}>
            Since <CaseTime value={z.seit} variant="relative" relativeLocale="en" /> · {seitQuelleLabel(z.seit_quelle)}
          </Typography>
          {/* 🔴 `data-testid="faelligkeit-frist"`: die FRISTZEILE als Ganzes, nicht
              ihre Endfragmente. `ui2-listen-karten` L.1g misst ihre Oberkante
              gegen die Oberkante der Karte („die Frist steht oben"). Ein
              Blatt-Muster allein reichte dafür nicht: eine Karte mit laufender
              Frist OHNE Ueberzug traegt gar kein passendes Blatt (das Datum
              passt auf kein Fragment-Muster), und die Probe haette sie nicht
              messen koennen (Regel 17 §1). */}
          <Typography
            data-testid="faelligkeit-frist"
            variant="caption"
            color="text.secondary"
            sx={KARTE_TEXT_SX}
          >
            {fristOffen ? (
              <>
                {/* 🔴 Wie in der Tabelle (`FristZelle`, R16-Runde-2-C2): Datum
                    und Quelle sind je EIN Stueck und brechen nicht in sich.
                    Der Leerraum zwischen den beiden `span`s ist die EINZIGE
                    Bruchstelle — er steht deshalb AUSSERHALB beider. Vorher
                    war die Quelle ein blosser Textknoten; auf dem Telefon
                    brach sie mitten im Satz („Case not acted" / „on · 2622 h
                    over", Foto `g12b4_zweig/390_grund.png`).
                    🔴 Der Trennpunkt gehoert zum FOLGENDEN Teil (R16-Runde-3-C3):
                    `{' '}` aussen, `'· '` INNERHALB des Quellen-`span`s — genau
                    die Form des Ueberzugs darunter und der Tabelle. Bricht die
                    Zeile, beginnt die neue mit „· "; passt alles, ist die
                    gerenderte Zeile unveraendert. Vorher stand `{' · '}` als
                    nackter Textknoten zwischen beiden, und der Browser durfte an
                    BEIDEN Leerzeichen brechen — dann blieb der Punkt am
                    Zeilenende haengen (Foto `g12b5_zweig/390_lang.png`). */}
                <Box component="span" data-testid="faelligkeit-frist-datum" sx={{ whiteSpace: 'nowrap' }}>
                  Due <CaseTime value={z.frist} variant="absolute" />
                </Box>
                {' '}
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {'· '}{fristQuelleLabel(z.frist_quelle)}
                </Box>
              </>
            ) : (
              // 🔴 Wie in der Tabelle (`FristZelle`): der Strich ist
              // `text.disabled`, die Worte bleiben `text.secondary`. Zwei
              // Knoten, weil eine `caption` nur eine Farbe trägt — und die
              // Worte stehen in einem eigenen `span`, damit die Probe ihre
              // Blätter findet (`ui2-listen-karten` L.1b misst den rechten Rand
              // genau dieser Blätter; ein blosser Textknoten hat kein Element).
              <>
                <Box component="span" sx={{ color: 'text.disabled' }}>{KEIN_WERT}</Box>
                {/* 🔴 `nowrap` — das Gegenstueck zum Ueberzug unten: die Angabe
                    „ · No deadline" ist EIN Stueck und bricht nicht mitten durch
                    (Probe `ui2-listen-karten` L.1f). */}
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{' · No deadline'}</Box>
              </>
            )}
            {z.frist_erreicht && z.frist_stunden_ueber != null ? (
              // `Box component="span"`: eine zweite `Typography` in einer
              // `caption` wäre eine zweite Textstimme in derselben Zeile, und
              // ein `<p>` in einem `<span>` ist ungültiges HTML.
              //
              // 🔴 `nowrap` auf dem GANZEN Fragment: „· 2243 h over" ist eine
              // Angabe, kein Fließtext. Ohne die Regel bricht sie mitten durch —
              // gemessen auf 390 px endete die Zeile mit „· 2243 h", und das
              // rote „over" stand allein in der zweiten Zeile. Passt das
              // Fragment nicht mehr, wandert es GESCHLOSSEN in die nächste Zeile
              // (Probe `ui2-listen-karten` L.1f).
              // 🔴 Der Leerraum steht AUSSERHALB (R16-Runde-2-C2): er ist die
              // Bruchstelle zwischen Quelle und Überzug, und innerhalb eines
              // `nowrap`-Teils wäre er unbrechbar.
              <>
                {' '}
                <Box component="span" sx={{ color: 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {'· '}{z.frist_stunden_ueber} h over
                </Box>
              </>
            ) : null}
          </Typography>
          {/* 🔴 `data-testid="faelligkeit-grund"`: ab G12b der KURZgrund —
              EINE Zeile, feste Hoehe (Plan §2.3 Nr. 7). `ui2-listen-karten`
              L.1e zieht seine Hoehe weiter von der Kartenhoehe ab und misst den
              FESTEN Teil der GESCHLOSSENEN Karte; der volle Satz liegt hinter
              dem Aufklapper (`faelligkeit-grund-voll`, L.1i) und zaehlt nicht
              mehr mit. */}
          <Typography
            data-testid="faelligkeit-grund"
            variant="caption"
            color="text.secondary"
            sx={KARTE_TEXT_SX}
          >
            {handlungKurzText(z)}
          </Typography>
        </Stack>
      }
      chips={
        // 🔴 Die Richtung steht in `chips`, nicht in `leading`. Als linkes
        // Merkmal schob sie die **Fallnummer** nach rechts — im DOM, im Bild
        // und in der Vorlesereihenfolge („Customer, RMA-2026-0458, …"). Die
        // Tabelle führt „Case" in der ersten Spalte; die Karte tut dasselbe,
        // sobald der Kopf frei ist. Der Chip ist ein Merkmal, kein Wert, und
        // `chips` ist genau sein Platz.
        <Pill
          tone={richtungTone}
          nowrap
          data-testid={`faelligkeit-richtung-${z.case_number}`}
          sx={z.richtung === 'unbekannt' ? BORDER_CUE_SX : undefined}
        >
          {richtungLabel(z.richtung)}
        </Pill>
      }
      // 🔴 Nur im OFFENEN Zustand (JSDoc `MobileRecordCard`: „im geschlossenen
      // Zustand gibt der Aufrufer `expanded` gar nicht mit"). Ein immer
      // gesetzter Knoten braechte den `mt: SP.chip`-Kasten auch geschlossen ins
      // Layout — 4 px, die der feste Teil nicht hat.
      expanded={offen ? <VollerGrund z={z} offen={offen} /> : null}
      fieldTestIds={{
        title: `karte-titel-${z.case_number}`,
        id: `karte-kennung-${z.case_number}`,
        chips: `karte-status-${z.case_number}`,
        meta: `karte-meta-${z.case_number}`,
      }}
    />
  )
}

/**
 * Nur noch die LISTE (Regel-16-B1/H6). Die Kachelreihe skelettiert in
 * `Verteilung` selbst, über `StatTile`s eigenes `loading`; die erste Fassung
 * hatte hier sechs eigene Kachel-Attrappen neben der echten Kachelbauart.
 *
 * Der Ladezustand der Liste ist ein `aria-busy` am umgebenden Papier (H6), kein
 * stummes Bild: ohne die Auszeichnung liest ein Screenreader acht Platzhalter
 * als acht Zeilen.
 */
function Listenskelett() {
  return (
    <Stack spacing={SP.tight}>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: PANEL_RADIUS }} />
      ))}
    </Stack>
  )
}

// ── Seite ───────────────────────────────────────────────────────────────────

export default function RMAFaelligkeit() {
  const theme = useTheme()
  // 🔴 `down('sm')` — dieselbe Weiche wie in `POInbox.jsx:752`,
  // `RMAInbox.jsx:2188`, `IncomingInventory.jsx:141` und
  // `VendorsOverview.jsx:209`. Eine zweite Grenze (etwa `(pointer: coarse)`)
  // hätte dieselbe Seite in zwei Breiten verschieden geformt, und der Nutzer
  // sähe die Form wechseln, ohne dass sich die Breite sichtbar ändert.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // 🔴 Der Listen-Zustand liegt in der ADRESSE (Plan §3.1): GENAU `page`, sonst
  // nichts. Kein `q` (es gibt keine Suche), kein `sort` (der Satz ist fest und
  // zusammengesetzt), kein `pageSize` (50 ist die Hausgroesse, keine
  // Nutzerwahl). Der Hook laesst unbekannte Parameter stehen.
  const query = useListQuery({ pageSize: ROWS_PER_PAGE })
  const { state, set } = query

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const laden = useCallback(() => {
    let alive = true
    setLoading(true)
    setError(null)
    axios.get(`${API}/api/rma/faelligkeit`)
      .then((r) => { if (alive) setData(r.data) })
      .catch((e) => {
        if (!alive) return
        // Der GRUND, nicht nur „es ging nicht": ein 401 liest sich anders als
        // ein 500, und ohne die Zahl sucht die nächste Person im falschen Teil.
        const status = e?.response?.status
        setError(status ? `Request failed with HTTP ${status}.` : (e?.message || 'Request failed.'))
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  useEffect(() => laden(), [laden])

  // Ein älterer Server ohne `handlung_reihenfolge` wird gewarnt, nicht
  // verschwiegen: die Kacheln stünden dann in einer Reihenfolge, die niemand
  // gewählt hat, und das sähe aus wie eine Entscheidung.
  useEffect(() => {
    if (data && !data.handlung_reihenfolge) {
      console.warn(
        'RMAFaelligkeit: die Nutzlast bringt `handlung_reihenfolge` nicht mit. '
        + 'Die Verteilungskacheln stehen in der Reihenfolge ihres ersten Auftretens; '
        + 'die Reihenfolge des Servers ist unbekannt.',
      )
    }
  }, [data])

  // 🔴 `useMemo`: die leere Liste waere sonst bei JEDEM Render ein neues Array,
  // und der `useMemo` des Seitenausschnitts darunter rechnete jedes Mal neu
  // (ESLint `react-hooks/exhaustive-deps`, zurecht).
  const zeilen = useMemo(() => data?.zeilen || [], [data])
  // 🔴 Seitenwechsel CLIENTSEITIG ueber `data.zeilen`: die Antwort traegt ALLE
  // offenen Faelle (`faelligkeit_api.py:64-79`). Der Sortiersatz bleibt stabil,
  // weil EINMAL serverseitig sortiert und dann nur geschnitten wird — genau
  // eine Stelle. Die Klemmung schreibt die URL NICHT um: die Ansicht zeigt die
  // letzte Seite, sie behauptet keine (Plan §3.2, N10.4).
  const seiten = Math.max(1, Math.ceil(zeilen.length / ROWS_PER_PAGE))
  const seite = Math.min(Math.max(1, Number(state.page) || 1), seiten)
  const sichtbareZeilen = useMemo(
    () => zeilen.slice((seite - 1) * ROWS_PER_PAGE, seite * ROWS_PER_PAGE),
    [zeilen, seite],
  )
  const verteilung = data?.verteilung || {}
  // 🔴 Die Reihenfolge kommt AUS DER NUTZLAST, und es gibt keinen eingebauten
  // Vorrat mehr (Regel-16-W2′). Ein solcher Vorrat wäre ein ungeprüfter Spiegel
  // der Backend-Konstante `HANDLUNG_REIHENFOLGE`: benennt das Backend einen
  // Eimer um, fällt der Filter leer, und die Kacheln stünden still in der
  // Schlüsselreihenfolge von `jsonify` - eine Reihenfolge, die niemand gewählt
  // hat, und die aussähe wie eine Entscheidung.
  //
  // Fehlt das Feld, ist die Reihenfolge des ersten Auftretens die einzige, die
  // diese Seite kennt. Der `useEffect` unten warnt darüber, statt sie
  // verschwiegen als gewählt auszugeben.
  const reihenfolge = data?.handlung_reihenfolge || Object.keys(verteilung)
  // Bekannte Eimer in der Reihenfolge des Servers, unbekannte hinten angehängt.
  // Ein neuer Eimer darf nicht stillschweigend aus der Summe fallen.
  //
  // 🔴 Solange die Antwort aussteht, steht KEIN Eimer: seinen Namen und seine
  // Position kennt die Seite erst mit der Nutzlast. Der Preis ist benannt - die
  // Reihe wächst einmal, wenn die Antwort landet. Die Alternative wäre ein
  // zweiter ungeprüfter Spiegel gewesen, nur diesmal über die Anzahl.
  const eimer = data
    ? [
      ...reihenfolge.filter((h) => h in verteilung),
      ...Object.keys(verteilung).filter((h) => !reihenfolge.includes(h)),
    ]
    : []

  return (
    <PageContainer width="full">
      {/* 🔴 Zone 1 (`liste-kopf`) — P2/P3 lesen sie. */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Due"
          breadcrumbs={[{ label: 'After-Sales', href: '/rma' }, { label: 'Due' }]}
        />
      </Box>

      {error ? (
        // Leerzustand 2 von 2: `error`. Eigener Wortlaut und eigene Handlung
        // (Wiederholung), nicht derselbe Kasten wie „kein offener Fall".
        //
        // 🔴 Und die Kachelreihe entfällt hier GANZ, statt Nullen zu zeigen: ein
        // gescheiterter Abruf, der „0 offene Fälle" malt, ist genau die
        // verwechselte Null, gegen die Regel 22 Punkt 3 steht.
        //
        // 🔴 Im `Panel` wie der Leerzustand daneben (R16-W5, Nachbesserung 2):
        // das Listenmuster setzt BEIDE Zustände in denselben Listenkasten
        // (`design/muster/ListenMuster.jsx`). Weggelassen bleibt allein die
        // KPI-Reihe — das ist P10 und bleibt so.
        <Panel>
          <EmptyState
            reason="error"
            message="Could not load the due-date view."
            detail={error}
            onRetry={laden}
          />
        </Panel>
      ) : (
        <Stack spacing={SP.section}>
          {/* 🔴 Zone 2 (`liste-kpi`) — die Kacheln bleiben ANZEIGE (OE-1 A):
              kein `onClick`, kein Hover, kein Tooltip. */}
          <Box data-testid="liste-kpi">
            <Verteilung data={data} loading={loading} eimer={eimer} />
          </Box>

          {!loading && (!data || data.gesamt === 0) ? (
            // Leerzustand 1 von 2: `no-data`. Der Grund steht als `detail`
            // daneben, inklusive des Tores, das über „offen" entscheidet.
            <Panel>
              <EmptyState
                reason="no-data"
                message="No open cases."
                detail={
                  'A case counts as open unless its status is one of: '
                  + `${(data?.offen_tor || []).join(', ') || KEIN_WERT}.`
                }
              />
            </Panel>
          ) : (
            // 🔴 Zwei Marken, zwei Elemente (Plan §2.3 Nr. 2): `liste-panel`
            // traegt die Zone (P8 zaehlt `role="status"` darin), `faelligkeit-liste`
            // bleibt am `Panel` — die Bestandsproben haengen daran.
            <Box data-testid="liste-panel">
              <Panel
                data-testid="faelligkeit-liste"
                aria-busy={loading ? 'true' : undefined}
              >
                {/* 🔴 Der Panel-Kopf steht IMMER, auch im Ladezustand
                    (R16-W3, Nachbesserung 2, 2026-10-24). Bis dahin lag er im
                    `else`-Zweig: beim Laden sah man acht Skelette, und mit der
                    Antwort erschienen rund 60 px Kopf darüber und schoben die
                    Liste nach unten. Der Prop `ResultCount.loading` war damit
                    an dieser Stelle tot. Rotweg der Probe: den Kopf zurück in
                    den `else`-Zweig ziehen — dann fehlt `sortiert-nach`
                    während des Ladens.
                    ⚠️ Der Zaehler bleibt während des Ladens LEER (keine 0); auf
                    den ERSTEN Abruf bleibt damit ein Sprung von der Höhe einer
                    Textzeile. Er wird hingenommen: die Alternative wäre eine
                    erfundene Zahl oder eine zweite Skelettbauart neben
                    `Listenskelett`. */}
                <Stack spacing={SP.chip} sx={{ mb: SP.tight }}>
                  {/* 🔴 Erste Zeile: links GENAU EINE Aussage (der Zaehler),
                      rechts „As of"; darunter der Sortiersatz, darunter die
                      Linien-Erklaerung (Plan §2.3 Nr. 6, W16). */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    flexWrap="wrap"
                    spacing={SP.chip}
                  >
                    <ResultCount
                      noun="cases"
                      // `m` ist der Bestand OHNE Vorgaben — hier ist beides
                      // gleich, weil nichts einengt. Waehrend des ERSTEN Ladens
                      // `null` (leere Region, kein Sprung auf 0); beim
                      // Nachladen bleibt die letzte Zahl stehen (das ist die
                      // dokumentierte Zusage von `ResultCount.loading`).
                      n={data?.gesamt ?? null}
                      m={data?.gesamt ?? null}
                      loading={loading}
                    />
                    {/* „As of" NUR mit Antwort: `CaseTime` malte sonst den
                        Strich, und „As of —" wäre eine Aussage über einen
                        Zeitpunkt, den es nicht gibt. */}
                    {data ? (
                      <Typography variant="caption" color="text.secondary">
                        As of <CaseTime value={data.generated_at} variant="absolute" withSeconds />
                      </Typography>
                    ) : null}
                  </Stack>
                  {/* 🔴 P18: kein Sortierbedienelement — der SATZ traegt die
                      Ordnung (`ProbeRoute.sortiertNach`). */}
                  <Typography
                    data-testid="sortiert-nach"
                    variant="caption"
                    color="text.secondary"
                  >
                    Sorted by action, breaches first, then oldest.
                  </Typography>
                  {/* Regel-16-H2: die gestrichelte Linie ist eine LinienFORM,
                      die ohne ihre Erklärung nicht trägt. Sie steht hier und
                      nicht in einem Tooltip, weil sie für die ganze Spalte
                      gilt und nicht für eine Zelle. */}
                  <Typography variant="caption" color="text.secondary">
                    A dashed outline means the direction map does not know this
                    value: the case is counted, not sorted.
                  </Typography>
                </Stack>
                {loading ? <Listenskelett /> : (
                  <>
                    {isMobile ? (
                      // Eine Liste, kein Haufen `div`s: die Zeilen sind
                      // Datensätze, und `<ul>` sagt das, ohne dass eine
                      // Bildschirmleserin die Zahl der Einträge raten muss.
                      // 🔴 `role="list"` gehört dazu: `list-style: none` nimmt dem
                      // `<ul>` in WebKit (Safari/VoiceOver) die Listen-Semantik —
                      // die Zahl der Einträge wäre dann wieder geraten. Die Rolle
                      // stellt genau das her, was der Satz darüber verspricht.
                      <Box data-testid="liste-karte">
                        <Stack component="ul" role="list" spacing={SP.tight} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                          {sichtbareZeilen.map((z) => (
                            <Box component="li" key={z.case_id}>
                              <Karte z={z} />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ) : (
                      <Box data-testid="liste-tabelle">
                        <TableViewport columns={SPALTEN_BREITEN}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Case</TableCell>
                                <TableCell>Waiting on</TableCell>
                                <TableCell>Since</TableCell>
                                <TableCell>Deadline</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sichtbareZeilen.map((z) => <Zeile key={z.case_id} z={z} />)}
                            </TableBody>
                          </Table>
                        </TableViewport>
                      </Box>
                    )}
                    {/* 🔴 Die Fusszeile steht NUR mit Inhalt (`> ROWS_PER_PAGE`):
                        `TableFooterSimple` zeigt den Blaetterer erst ab
                        `count > rowsPerPage`; darunter bliebe ein leerer,
                        deckender Balken (Hausform G12a, R16 W1). */}
                    {zeilen.length > ROWS_PER_PAGE && (
                      <Box data-testid="liste-fuss">
                        <TableFooterSimple
                          count={zeilen.length}
                          page={seite - 1}
                          onPageChange={(p) => set('page', p + 1)}
                          rowsPerPage={ROWS_PER_PAGE}
                          // BS17: mobil steht die Leiste im Fluss nach der
                          // letzten Karte, statt ueber der schwebenden
                          // Unterleiste zu kleben. Kein `leftSlot`.
                          stickyOnMobile={false}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Panel>
            </Box>
          )}
        </Stack>
      )}
    </PageContainer>
  )
}
